/** Keep one store's screen and register values under its store ID, and mirror the open store into the names the register already reads. */

const ACTIVE = "storeScopeActive";
const READY = "storeScopeReady";
const EVENT = "nuage-store-scope";
const ENVS = ["dev", "essai", "prod"];

const PLAIN = [
  "keyboardEnabled",
  "pos_screen_scale",
  "pos_color_mode",
  "pos_color_mode_override",
  "pos_color_mode_default",
  "rqExchangeWindow",
  "rqExchangeLog",
  "pos_startInFullscreen",
  "pos_receipt_email_template",
  "lastPaidOrderData",
  "lastPaidOrderId",
  "customerOrder",
  "lanSyncUploadQueue",
  "tableSessionData",
  "checkoutTimerEndTime",
];

const ENV_PREFIXES = [
  "company_info",
  "transHeader",
  "csr_form",
  "requestConstant",
  "mev_operation_logs",
  "mev_terminals",
  "store_id",
  "terminal_csr",
  "terminal_privateKey",
  "terminal_publicKey",
];

const SIGNATURES = ["signa_actu", "signa_preced", "signa_previous"];

/** Saved only as `name_storeId`. The name without a store ID is not stored. */
const STORE_ONLY = ["Settings_envrn", "receipt_text_envrn", "storeTimeZone"];

function legacyNames() {
  const names = [...PLAIN];
  ENVS.forEach((env) => {
    ENV_PREFIXES.forEach((prefix) => names.push(`${prefix}_${env}`));
    SIGNATURES.forEach((name) => names.push(`${name}_${env}`));
  });
  return names;
}

function storeName(legacy: string, storeId: string) {
  for (const env of ENVS) {
    if (!legacy.endsWith(`_${env}`)) continue;
    const prefix = legacy.slice(0, -(env.length + 1));
    if (ENV_PREFIXES.includes(prefix) || SIGNATURES.includes(prefix)) {
      return `${prefix}_${storeId}_${env}`;
    }
  }
  return `${legacy}_${storeId}`;
}

function read(name: string) {
  try {
    return localStorage.getItem(name);
  } catch {
    return null;
  }
}

let rawGet: ((key: string) => string | null) | null = null;
let rawSet: ((key: string, value: string) => void) | null = null;
let rawRemove: ((key: string) => void) | null = null;
let mirroring = false;

function write(name: string, value: string | null) {
  const set = rawSet || localStorage.setItem.bind(localStorage);
  const remove = rawRemove || localStorage.removeItem.bind(localStorage);
  mirroring = true;
  try {
    if (value == null) remove(name);
    else set(name, value);
  } catch {
    // Storage can be blocked. The open store still has its previous copy.
  } finally {
    mirroring = false;
  }
}

function openStoreId() {
  return read(ACTIVE) || read("selectedStore") || "";
}

function installStoreWriteMirror() {
  if (rawSet || typeof localStorage === "undefined") return;
  rawGet = localStorage.getItem.bind(localStorage);
  rawSet = localStorage.setItem.bind(localStorage);
  rawRemove = localStorage.removeItem.bind(localStorage);
  const names = new Set(legacyNames());
  localStorage.getItem = (key: string) => rawGet!(key);
  localStorage.setItem = (key: string, value: string) => {
    rawSet!(key, value);
    if (mirroring || !names.has(key)) return;
    const storeId = openStoreId();
    if (!storeId) return;
    mirroring = true;
    try {
      rawSet!(storeName(key, storeId), value);
    } catch {
      // The unsuffixed copy remains for the open store.
    } finally {
      mirroring = false;
    }
  };
  localStorage.removeItem = (key: string) => {
    rawRemove!(key);
    if (mirroring || !names.has(key)) return;
    const storeId = openStoreId();
    if (!storeId) return;
    mirroring = true;
    try {
      rawRemove!(storeName(key, storeId));
    } catch {
      // The store copy remains until the next save.
    } finally {
      mirroring = false;
    }
  };
}

function scopedStoreKey(name: string) {
  if (!STORE_ONLY.includes(name)) return "";
  const storeId = openStoreId();
  return storeId ? `${name}_${storeId}` : "";
}

/** Drop the duplicate that has no store ID. Leave the store-id key as it is. */
function dropBareStoreKeys(storeId: string) {
  if (!rawGet || !rawSet || !rawRemove) return;
  STORE_ONLY.forEach((name) => {
    const scoped = `${name}_${storeId}`;
    if (rawGet!(scoped) == null) {
      const bare = rawGet!(name);
      if (bare != null) rawSet!(scoped, bare);
    }
    if (rawGet!(name) != null) rawRemove!(name);
  });
}

export function readStoreLocal(name: string) {
  const key = scopedStoreKey(name);
  if (!key || typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStoreLocal(name: string, value: string) {
  const key = scopedStoreKey(name);
  if (!key || typeof localStorage === "undefined") return;
  localStorage.setItem(key, value);
  if (localStorage.getItem(name) != null) localStorage.removeItem(name);
}

/** The certificate pointer belongs to one store. Drop the copy that has no store ID. */
function claimLooseThumbprints(storeId: string) {
  ENVS.forEach((env) => {
    const loose = `mev_current_thumbprint_${env}`;
    const scoped = `mev_current_thumbprint_${storeId}_${env}`;
    const looseValue = read(loose);
    if (looseValue == null) return;
    if (read(scoped) == null) write(scoped, looseValue);
    write(loose, null);
  });
}

function trainingLegacyKeys() {
  const keys: string[] = [];
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index) || "";
      if (!key.startsWith("trainingMode_")) continue;
      const userId = key.slice("trainingMode_".length);
      if (!userId || userId.includes("_")) continue;
      keys.push(key);
    }
  } catch {
    // Ignore a storage scan that cannot run.
  }
  return keys;
}

function trainingStoreKeys(storeId: string) {
  const prefix = `trainingMode_${storeId}_`;
  const keys: string[] = [];
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index) || "";
      if (key.startsWith(prefix)) keys.push(key);
    }
  } catch {
    // Ignore a storage scan that cannot run.
  }
  return keys;
}

function saveTraining(storeId: string) {
  const prefix = `trainingMode_${storeId}_`;
  const live = new Set<string>();
  trainingLegacyKeys().forEach((key) => {
    const userId = key.slice("trainingMode_".length);
    live.add(userId);
    write(`${prefix}${userId}`, read(key));
  });
  trainingStoreKeys(storeId).forEach((key) => {
    const userId = key.slice(prefix.length);
    if (!live.has(userId)) write(key, null);
  });
}

function loadTraining(storeId: string) {
  trainingLegacyKeys().forEach((key) => write(key, null));
  const prefix = `trainingMode_${storeId}_`;
  trainingStoreKeys(storeId).forEach((key) => {
    const userId = key.slice(prefix.length);
    write(`trainingMode_${userId}`, read(key));
  });
}

function saveStore(storeId: string) {
  legacyNames().forEach((legacy) => {
    const value = read(legacy);
    if (value != null) write(storeName(legacy, storeId), value);
  });
  saveTraining(storeId);
}

function loadStore(storeId: string) {
  legacyNames().forEach((legacy) => {
    write(legacy, read(storeName(legacy, storeId)));
  });
  loadTraining(storeId);
}

function fillMissing(storeId: string) {
  legacyNames().forEach((legacy) => {
    const scoped = storeName(legacy, storeId);
    const live = read(legacy);
    if (live != null && read(scoped) !== live) write(scoped, live);
  });
  saveTraining(storeId);
}

export function adoptStore(storeId: string) {
  const id = String(storeId || "").trim();
  if (!id || typeof localStorage === "undefined") return;
  installStoreWriteMirror();
  const active = read(ACTIVE) || "";
  if (active === id) {
    fillMissing(id);
    dropBareStoreKeys(id);
    claimLooseThumbprints(id);
    return;
  }
  if (active) {
    saveStore(active);
    dropBareStoreKeys(active);
  }
  if (read(READY) !== "1") {
    saveStore(id);
    write(READY, "1");
  } else {
    loadStore(id);
  }
  write(ACTIVE, id);
  dropBareStoreKeys(id);
  claimLooseThumbprints(id);
  window.dispatchEvent(new Event(EVENT));
  window.dispatchEvent(new Event("rq-exchange-log"));
}

export function releaseStoreScope() {
  if (typeof localStorage === "undefined") return;
  const active = read(ACTIVE) || read("selectedStore") || "";
  if (active) {
    saveStore(active);
    dropBareStoreKeys(active);
  }
  write(ACTIVE, null);
}

/** Remove the open session's unsuffixed copies. Keys that already contain a store ID stay. */
export function clearOpenSessionMirrors() {
  if (typeof localStorage === "undefined") return;
  legacyNames().forEach((legacy) => write(legacy, null));
  trainingLegacyKeys().forEach((key) => write(key, null));
}

export const STORE_SCOPE_EVENT = EVENT;

